import Phaser from "phaser";
import { COLORS, FONTS } from "./theme";

export type KeycapGlyph = "left" | "right" | "up" | "down" | "esc";

const SIZE = 72;

export function keycapTextureKey(glyph: KeycapGlyph): string {
  return `keycap-${glyph}`;
}

export function ensureControlKeycaps(scene: Phaser.Scene): void {
  const glyphs: KeycapGlyph[] = ["left", "right", "up", "down", "esc"];
  for (const glyph of glyphs) {
    const key = keycapTextureKey(glyph);
    if (scene.textures.exists(key)) {
      continue;
    }
    paintKeycap(scene, key, glyph);
  }
}

function paintKeycap(scene: Phaser.Scene, key: string, glyph: KeycapGlyph): void {
  const texture = scene.textures.createCanvas(key, SIZE, SIZE);
  const ctx = texture?.getContext();
  if (!texture || !ctx) {
    return;
  }

  const x = 7;
  const y = 5;
  const w = 54;
  const h = 54;
  const r = 12;

  ctx.clearRect(0, 0, SIZE, SIZE);

  ctx.fillStyle = "rgba(42, 26, 16, 0.28)";
  roundRect(ctx, x + 2, y + 5, w, h, r);
  ctx.fill();

  ctx.fillStyle = "#8a5a2e";
  roundRect(ctx, x, y + 1, w, h, r);
  ctx.fill();

  const rim = ctx.createLinearGradient(x, y, x + w, y + h);
  rim.addColorStop(0, "#e2c48a");
  rim.addColorStop(0.45, "#c49a5c");
  rim.addColorStop(1, "#6e4320");
  ctx.fillStyle = rim;
  roundRect(ctx, x, y, w, h - 1, r);
  ctx.fill();

  const face = ctx.createLinearGradient(x + 6, y + 5, x + 6, y + h - 10);
  face.addColorStop(0, "#fff4dc");
  face.addColorStop(0.55, "#f3e6c8");
  face.addColorStop(1, "#e4d0a4");
  ctx.fillStyle = face;
  roundRect(ctx, x + 6, y + 5, w - 12, h - 14, 8);
  ctx.fill();

  ctx.fillStyle = COLORS.moss;
  if (glyph === "esc") {
    ctx.font = `700 17px ${FONTS.display}`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("Esc", x + w / 2, y + h / 2 - 2);
  } else {
    drawArrow(ctx, x + w / 2, y + h / 2 - 1, glyph, 22);
  }

  texture.refresh();
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  const radius = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.arcTo(x + w, y, x + w, y + h, radius);
  ctx.arcTo(x + w, y + h, x, y + h, radius);
  ctx.arcTo(x, y + h, x, y, radius);
  ctx.arcTo(x, y, x + w, y, radius);
  ctx.closePath();
}

function drawArrow(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  dir: Exclude<KeycapGlyph, "esc">,
  size: number,
): void {
  const head = size * 0.46;
  const half = size * 0.42;
  const shaft = size * 0.16;
  const shaftLen = size * 0.34;
  ctx.beginPath();
  if (dir === "up") {
    ctx.moveTo(cx, cy - head);
    ctx.lineTo(cx - half, cy + size * 0.12);
    ctx.lineTo(cx - shaft, cy + size * 0.12);
    ctx.lineTo(cx - shaft, cy + head);
    ctx.lineTo(cx + shaft, cy + head);
    ctx.lineTo(cx + shaft, cy + size * 0.12);
    ctx.lineTo(cx + half, cy + size * 0.12);
  } else if (dir === "down") {
    ctx.moveTo(cx, cy + head);
    ctx.lineTo(cx - half, cy - size * 0.12);
    ctx.lineTo(cx - shaft, cy - size * 0.12);
    ctx.lineTo(cx - shaft, cy - head);
    ctx.lineTo(cx + shaft, cy - head);
    ctx.lineTo(cx + shaft, cy - size * 0.12);
    ctx.lineTo(cx + half, cy - size * 0.12);
  } else if (dir === "left") {
    ctx.moveTo(cx - head, cy);
    ctx.lineTo(cx + size * 0.12, cy - half);
    ctx.lineTo(cx + size * 0.12, cy - shaft);
    ctx.lineTo(cx + shaftLen, cy - shaft);
    ctx.lineTo(cx + shaftLen, cy + shaft);
    ctx.lineTo(cx + size * 0.12, cy + shaft);
    ctx.lineTo(cx + size * 0.12, cy + half);
  } else {
    ctx.moveTo(cx + head, cy);
    ctx.lineTo(cx - size * 0.12, cy - half);
    ctx.lineTo(cx - size * 0.12, cy - shaft);
    ctx.lineTo(cx - shaftLen, cy - shaft);
    ctx.lineTo(cx - shaftLen, cy + shaft);
    ctx.lineTo(cx - size * 0.12, cy + shaft);
    ctx.lineTo(cx - size * 0.12, cy + half);
  }
  ctx.closePath();
  ctx.fill();
}
